import React, { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Origin,
  ReferenceSource,
  ReferenceType,
} from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/request/GetReferencesStockRequest';
import { CatalogSource } from '@1po/1po-bff-fe-spec/generated/common/vehicle/CatalogSource';
import { RootState } from 'app/AppStore';
import { DataContainer } from 'components/DataContainer';
import { ExadisInterestedButton } from 'components/ExadisInterestedButton';
import OrderActionsSection from 'components/ReferencePriceSection/OrderActionsSection';
import { PRICE_WRAPPER_MAX_WIDTH_NARROW, PriceErrorDisplay } from 'components/ReferencePriceSection/PriceErrorDisplay';
import PriceSection from 'components/ReferencePriceSection/PriceSection';
import { PriceUnavailable } from 'components/ReferencePriceSection/PriceUnavailable';
import { PRICE_WRAPPER_MAX_WIDTH } from 'components/ReferencePriceSection/ReferencePriceSectionUtils';
import { DeliveryInformationUnavailable } from 'components/ReferenceUnavailableBox/DeliveryInformationUnavailable';
import { ReferenceUnavailableForOrder } from 'components/ReferenceUnavailableBox/ReferenceUnavailable';
import PrivateComponent from 'composers/PrivateComponent';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { createIAMSupplierReferenceKey, DATAHUB, IAM } from 'domains/catalog/Catalog.types';
import { getDHReference, getIAMReference, getIsStockAvailable, getPrice, getStockInfo } from 'domains/references';
import { useFetchPrices } from 'domains/references/References.requests';
import { getUserRights, SparePartsViewType, UserRole } from 'domains/user';
import { ButtonShape, Flex, MarginBox, Spin } from 'UI';
import { getData, getTractionBatteryOrderStatus, resolveTractionBatteryFamilyCode } from 'utils';

const PriceNotFound = ({ narrow, hasStockAtR1 }: { narrow?: boolean; hasStockAtR1: boolean }) => (
  <Flex direction={'row'} justify={'space-between'} align={'center'} gap={20}>
    <PriceUnavailable narrow={narrow} hasStockAtR1={hasStockAtR1} />
  </Flex>
);

const PriceNotFoundWithExadis = ({
  narrow,
  hasStockAtR1,
  isExadis,
  catalogSource,
  referenceNumber,
  hideButtons,
}: {
  narrow?: boolean;
  hasStockAtR1: boolean;
  isExadis?: boolean;
  catalogSource: CatalogSource;
  referenceNumber: string;
  hideButtons: boolean;
}) => (
  <>
    <PriceNotFound narrow={narrow} hasStockAtR1={hasStockAtR1} />
    {isExadis && !hideButtons && (
      <ExadisInterestedButton catalogSource={catalogSource} referenceNumber={referenceNumber} />
    )}
  </>
);

const PriceLoading = () => <Spin size={'small'} />;

const PriceError = ({ narrow }: { narrow?: boolean }) => <PriceErrorDisplay narrow={narrow} />;

export interface ReferenceCardPriceWrapperProps {
  referenceNumber: string;
  referenceType: ReferenceType;
  catalogSource: CatalogSource;
  handleAddToCartClick: (sellerName?: string, sellerNumber?: string) => void;
  sparePartsView?: SparePartsViewType;
  vehicleKey?: string;
  align?: 'left' | 'right';
  isApplicableToCurrentVehicle?: boolean;
  hideDiscountSection?: boolean;
  narrow?: boolean;
  showFirstHelp?: boolean;
  useCompactView?: boolean;
  availableForOrder?: boolean;
  origin?: Origin;
  supplierCode?: string;
  referenceSource?: ReferenceSource;
  enabled?: boolean;
  isMKTP?: boolean;
  isExadis?: boolean;
  clientDiscount?: number;
  withIcon?: boolean;
  buttonSize?: 'small' | 'middle' | 'large';
  buttonShape?: ButtonShape;
  isAvailableSoon?: boolean;
  isNoLongerAvailable?: boolean;
  linkedReferences?: string[];
  hideButtons?: boolean;
  hidePrice?: boolean;
  externalButton?: ReactNode;
  handleClickCompare?: (reference: string) => void;
  handleMultipleOffersButtonClick?: React.Dispatch<React.SetStateAction<boolean>>;
  sellerNumber?: string;
  sellerName?: string;
}

export const ReferencePriceSection = React.memo(function ReferenceCardPriceWrapper({
  referenceNumber,
  sparePartsView,
  handleAddToCartClick,
  isApplicableToCurrentVehicle = false,
  vehicleKey,
  align = 'right',
  hideDiscountSection,
  catalogSource,
  referenceType,
  narrow,
  showFirstHelp = false,
  useCompactView = false,
  availableForOrder = true,
  origin,
  supplierCode,
  referenceSource,
  enabled = true,
  isMKTP = false,
  isExadis = false,
  clientDiscount,
  withIcon,
  buttonSize,
  buttonShape,
  isAvailableSoon,
  isNoLongerAvailable,
  linkedReferences = undefined,
  hidePrice,
  hideButtons = false,
  externalButton,
  handleClickCompare,
  handleMultipleOffersButtonClick,
  sellerNumber,
  sellerName,
}: ReferenceCardPriceWrapperProps) {
  const { t } = useTranslation();
  const referenceKey = createIAMSupplierReferenceKey(referenceNumber, supplierCode);
  const price = useSelector((state: RootState) => getPrice(state, referenceNumber, sellerNumber));
  const stock = useSelector((state: RootState) => getStockInfo(state, referenceNumber, sellerNumber));
  const stockAvailable = useSelector((state: RootState) => getIsStockAvailable(state, referenceNumber));
  const availableInUserCountry = catalogSource === DATAHUB ? stockAvailable : true;
  const userRoles = useSelector(getUserRights);
  const resolvedUserRoles = Array.isArray(userRoles) ? userRoles : undefined;
  const vehicleDetail = useSelector(getLastVehicleDetail);
  const dhReference = useSelector((state: RootState) => getDHReference(state, { referenceNumber, vehicleKey }));
  const iamReference = useSelector((state: RootState) =>
    getIAMReference(state, {
      referenceNumber: catalogSource === IAM ? referenceKey : referenceNumber,
      vehicleKey,
    }),
  );
  const iamReferenceFallback = useSelector((state: RootState) =>
    getIAMReference(state, {
      referenceNumber: catalogSource === IAM ? referenceKey : referenceNumber,
      vehicleKey,
    }),
  );
  const getFamilyCode = (): string | undefined => {
    if (catalogSource === DATAHUB) {
      return resolveTractionBatteryFamilyCode(getData(dhReference)?.familyCode);
    }
    if (catalogSource === IAM) {
      return resolveTractionBatteryFamilyCode(
        getData(iamReference)?.commercialFamily,
        getData(iamReferenceFallback)?.commercialFamily,
      );
    }
    return undefined;
  };
  const familyCode = getFamilyCode();
  const { orderBlocked, orderBlockedMessage: orderBlockedMessageConfig } = getTractionBatteryOrderStatus({
    userRoles: resolvedUserRoles,
    vin: vehicleDetail?.vin,
    isCompatible: isApplicableToCurrentVehicle,
    familyCode,
  });
  const orderBlockedMessage = orderBlockedMessageConfig
    ? t(orderBlockedMessageConfig.key, orderBlockedMessageConfig.defaultMessage)
    : undefined;
  const canOrder = availableForOrder && !orderBlocked;
  useFetchPrices([referenceKey, ...(linkedReferences ?? [])], vehicleKey);

  const hasStockAtR1 = stock?.data?.warehouses.find(
    (warehouse) => warehouse.type === 'LOCAL' && warehouse.availableQuantity > 0,
  );
  const renderPriceNotFound = useCallback(
    () => (
      <PriceNotFoundWithExadis
        narrow={narrow}
        hasStockAtR1={!!hasStockAtR1}
        isExadis={isExadis}
        catalogSource={catalogSource}
        referenceNumber={referenceNumber}
        hideButtons={hideButtons}
      />
    ),
    [catalogSource, hasStockAtR1, isExadis, narrow, referenceNumber, hideButtons],
  );
  const renderPriceError = useCallback(() => <PriceError narrow={narrow} />, [narrow]);
  const renderPriceLoading = useCallback(() => <PriceLoading />, []);
  const getMaxWidth = () => {
    if (useCompactView) {
      return 'initial';
    }
    return narrow ? PRICE_WRAPPER_MAX_WIDTH_NARROW : PRICE_WRAPPER_MAX_WIDTH;
  };

  return (
    <PrivateComponent
      requiredRights={[UserRole.COMMAND, UserRole.CONNECT_COMMANDE]}
      render={() => (
        <>
          {availableInUserCountry ? (
            <DataContainer
              data={price}
              NotFound={renderPriceNotFound}
              ErrorState={renderPriceError}
              Loading={renderPriceLoading}
            >
              <Flex
                direction={useCompactView ? 'row' : 'column'}
                maxWidth={getMaxWidth()}
                justify={align === 'left' ? 'flex-start' : 'flex-end'}
                align={useCompactView ? 'center' : 'inherit'}
              >
                {!hidePrice && (
                  <PriceSection
                    referenceNumber={referenceNumber}
                    referenceType={referenceType}
                    sparePartsView={sparePartsView}
                    useCompactView={useCompactView}
                    isMKTP={isMKTP}
                    hideDiscountSection={hideDiscountSection}
                    clientDiscount={clientDiscount}
                    isAvailableSoon={isAvailableSoon}
                    isNoLongerAvailable={isNoLongerAvailable}
                    align={align}
                    narrow={narrow}
                    sellerNumber={sellerNumber}
                  />
                )}
                {orderBlocked && (
                  <MarginBox mt={10}>
                    <ReferenceUnavailableForOrder enabled message={orderBlockedMessage} />
                  </MarginBox>
                )}
                {useCompactView && <MarginBox ml={15} />}
                {!hideButtons && (
                  <OrderActionsSection
                    referenceNumber={referenceNumber}
                    referenceType={referenceType}
                    catalogSource={catalogSource}
                    handleAddToCartClick={handleAddToCartClick}
                    vehicleKey={vehicleKey}
                    isApplicableToCurrentVehicle={isApplicableToCurrentVehicle}
                    availableForOrder={canOrder}
                    isAvailableSoon={isAvailableSoon}
                    isExadis={isExadis}
                    isNoLongerAvailable={isNoLongerAvailable}
                    enabled={enabled}
                    origin={origin}
                    supplierCode={supplierCode}
                    referenceSource={referenceSource}
                    withIcon={withIcon}
                    showFirstHelp={showFirstHelp}
                    useCompactView={useCompactView}
                    externalButton={externalButton}
                    buttonSize={buttonSize}
                    buttonShape={buttonShape}
                    handleClickCompare={handleClickCompare}
                    handleMultipleOffersButtonClick={handleMultipleOffersButtonClick}
                    isMKTP={isMKTP}
                    sellerNumber={sellerNumber}
                    sellerName={sellerName}
                  />
                )}
              </Flex>
            </DataContainer>
          ) : (
            <DeliveryInformationUnavailable />
          )}
        </>
      )}
    />
  );
});
